import * as ko from "knockout"
import {IExportQueue, IObjectData} from "../../../index"
import {global, lm} from "../../../appContext"
import {_d} from "../../../util/misc"
import {getTime} from "../../../util/datetime"
import PromiseRequest from "../../../req/PromiseRequest"
import {nsPassword} from "../nsPassword"
import detailsHtml from './details.html?raw';

export const template: string = detailsHtml

const POLL_INTERVAL_MS = 10_000;
// Matches the BE TAP value cleanup window in AzureExport.CleanupExpiredTapValues.
const TAP_DISPLAY_MAX_AGE_MS = 10 * 60 * 1000;

export interface ITapParams {
    objectData: KnockoutObservable<IObjectData>
}

type TTapStep = 'idle' | 'requesting' | 'polling' | 'success' | 'error'

interface ITapValue {
    passcode: string
    expiresAt: string
}

export class viewModel {

    private readonly req_tap = new PromiseRequest('requesttemporaryaccesspass')
    private readonly req_getobject: PromiseRequest = global.getPromiseRequest('getobject')

    private step: KnockoutObservable<TTapStep> = ko.observable<TTapStep>('idle')
    private passcode: KnockoutObservable<string | null> = ko.observable(null)
    private expiresAt: KnockoutObservable<string | null> = ko.observable(null)
    private errorMessage: KnockoutObservable<string | null> = ko.observable(null)
    private copied: KnockoutObservable<boolean> = ko.observable(false)
    private copyLabelKey: KnockoutComputed<string> = ko.pureComputed(
        (): string => `tap.copy|n:${this.copied() ? 2 : 1}`,
    )
    private errorDisplay: KnockoutComputed<string> = ko.pureComputed((): string => {
        const msg = this.errorMessage()
        if (!msg) return ''
        return msg === 'tap.error.unknown' ? (lm.t('tap.error.unknown') ?? '') : msg
    })
    private expiresAtLabel: KnockoutComputed<string> = ko.pureComputed((): string => {
        const at = this.expiresAt()
        if (!at) return ''
        return lm.t({k: 'tap.expires.at', r: {time: getTime(at, true)}}) ?? ''
    })

    private timeoutId: number | null = null
    private isDisposed = false
    private readonly objectData: KnockoutObservable<IObjectData>

    constructor(params: ITapParams) {
        this.objectData = params.objectData
        const obj = params.objectData?.()
        if (obj) {
            this.checkExistingTap(obj)
        }
    }

    private checkExistingTap(obj: IObjectData) {
        const row = this.findTapRow(obj.exportqueue)
        if (!row) return

        if (row.exported && row.targetvalue) {
            this.showTapFromRow(row)
        } else if (row.exported && row.lasterror) {
            this.errorMessage(row.lasterror)
            this.step('error')
        } else if (!row.exported && row.retrycount >= 3) {
            this.errorMessage(row.lasterror || 'tap.error.unknown')
            this.step('error')
        } else if (!row.exported) {
            this.step('polling')
            this.scheduleNext(0)
        }
    }

    private findTapRow(queue: IExportQueue[]): IExportQueue | null {
        if (!queue?.length) return null
        const matches = queue.filter(r =>
            r.action?.toLowerCase() === 'password'
            && r.targetattribute === 'userTAP'
        )
        if (!matches.length) return null
        const first = matches[0];
        if (!first) return null;
        return matches.reduce((latest, r) => r.id > latest.id ? r : latest, first)
    }

    private showTapFromRow(row: IExportQueue) {
        try {
            const val: ITapValue = JSON.parse(row.targetvalue ?? '{}')
            if (!this.isTapDisplayable(row, val)) {
                this.resetTapState()
                return
            }
            this.passcode(val.passcode)
            this.expiresAt(val.expiresAt)
            this.step('success')
        } catch {
            this.errorMessage(row.lasterror || 'tap.error.unknown')
            this.step('error')
        }
    }

    private isTapDisplayable(row: IExportQueue, val: ITapValue): boolean {
        const exportedAt = nsPassword.parseServerDate(row.exported)
        if (exportedAt !== null && Date.now() - exportedAt > TAP_DISPLAY_MAX_AGE_MS) {
            return false
        }
        const expiresAt = nsPassword.parseServerDate(val.expiresAt)
        if (expiresAt !== null && expiresAt <= Date.now()) {
            return false
        }
        return !!val.passcode
    }

    private resetTapState() {
        this.passcode(null)
        this.expiresAt(null)
        this.errorMessage(null)
        this.step('idle')
    }

    private onRequestClick() {
        this.step('requesting')
        this.req_tap.abort()
        const userId = global.login.user()?.id
        if (!userId) {
            return
        }
        this.req_tap.request({id: userId})
            .then(() => {
                this.step('polling')
                this.scheduleNext(0)
            })
            .catch((reason: string) => {
                this.errorMessage(reason || 'tap.error.unknown')
                this.step('error')
            })
    }

    private scheduleNext(delay: number = POLL_INTERVAL_MS) {
        if (this.isDisposed) return
        this.clearTimeout()
        this.timeoutId = window.setTimeout(() => this.poll(), delay)
    }

    private clearTimeout() {
        if (this.timeoutId !== null) {
            window.clearTimeout(this.timeoutId)
            this.timeoutId = null
        }
    }

    private async poll() {
        if (this.isDisposed) return
        try {
            global.cacheManager.removeCache('getobject')
            this.req_getobject.abort()
            const user = global.login.user();
            if (!user) {
                return;
            }
            const data: IObjectData[] = await this.req_getobject.request(
                {id: user.id, manual: false},
                false,
                {},
                {useCacheIfAvailable: false},
            )
            if (this.isDisposed) return

            const obj = data?.[0]
            if (obj) this.objectData?.(obj)
            const row = obj ? this.findTapRow(obj.exportqueue) : null

            if (row && row.exported && row.targetvalue) {
                this.showTapFromRow(row)
                return
            }

            if (row && row.exported && row.lasterror) {
                this.errorMessage(row.lasterror)
                this.step('error')
                return
            }

            if (row && row.exported && !row.targetvalue) {
                this.resetTapState()
                return
            }

            if (row && row.retrycount >= 3) {
                this.errorMessage(row.lasterror || 'tap.error.unknown')
                this.step('error')
                return
            }
        } catch (err) {
            _d.warn('TAP poll failed', err)
        }

        if (this.isDisposed) return
        if (this.step() === 'polling') {
            this.scheduleNext()
        }
    }

    private onCopyClick() {
        const code = this.passcode()
        if (!code) return

        navigator.clipboard.writeText(code)
            .then(() => {
                this.copied(true)
                window.setTimeout(() => this.copied(false), 2000)
            })
            .catch(err => _d.warn('TAP copy failed', err))
    }

    private onRetryClick() {
        this.errorMessage(null)
        this.step('idle')
    }

    private onRequestNewClick() {
        this.passcode(null)
        this.expiresAt(null)
        this.copied(false)
        this.onRequestClick()
    }

    public dispose() {
        this.isDisposed = true
        this.clearTimeout()
        this.req_tap.abort()
        this.req_getobject.abort()
    }
}
